import Icon from "../components/Icon";
import { PageTitle, Btn } from "../components";
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../api";
import { recordUiError } from "../observability";

const PAGE_SIZE = 50;

const STATUS_CONFIG = {
  pending:   { label: "Pendente",        color: "#cbd5e1", bg: "rgba(148,163,184,.12)" },
  queued:    { label: "Na fila",          color: "#fcd34d", bg: "rgba(234,179,8,.12)" },
  processing:{ label: "Processando",      color: "#fdba74", bg: "rgba(249,115,22,.12)" },
  accepted:  { label: "Aceita pela Meta", color: "#a5b4fc", bg: "rgba(99,102,241,.15)" },
  sent:      { label: "Enviada ao WhatsApp", color: "#93c5fd", bg: "rgba(59,130,246,.14)" },
  delivered: { label: "Entregue",         color: "#86efac", bg: "rgba(34,197,94,.12)" },
  read:      { label: "Lida no WhatsApp",  color: "#6ee7b7", bg: "rgba(16,185,129,.15)" },
  failed:    { label: "Falhou",           color: "#fca5a5", bg: "rgba(239,68,68,.12)" },
  dead_letter:{ label: "Intervenção necessária", color: "#fecaca", bg: "rgba(239,68,68,.18)" },
  cancelled: { label: "Cancelada",        color: "#cbd5e1", bg: "rgba(148,163,184,.12)" },
};

const STATUS_FILTROS = ["", "pending", "queued", "processing", "accepted", "sent", "delivered", "read", "failed", "dead_letter", "cancelled"];

const ERRO_OPERACIONAL = {
  "131026": "Não foi possível entregar. Confira se o número está correto e possui WhatsApp.",
  "131050": "O destinatário não autorizou ou cancelou o recebimento de mensagens.",
  RECIPIENT_OPT_OUT: "O destinatário pediu para não receber mensagens.",
  INVALID_RECIPIENT_PHONE: "Telefone ausente ou inválido. Corrija o cadastro antes de reenviar.",
  AMBIGUOUS_PROVIDER_RESULT: "A Meta recebeu a chamada, mas o resultado ficou incerto. Não reenvie sem conferir.",
};

const getErroOperacional = (notification) => {
  if (!notification?.erro_code && !notification?.erro_message) return null;
  return ERRO_OPERACIONAL[String(notification.erro_code || "")]
    || notification.erro_message
    || `Falha técnica ${notification.erro_code}`;
};

const formatarData = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatarTelefone = (tel) => {
  const d = String(tel || "").replace(/\D/g, "");
  if (d.length >= 12) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9, 13)}`.replace(/-$/, "");
  return tel || "—";
};

const formatarNomeVariavel = (nome) => String(nome || "")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letra) => letra.toUpperCase());

const listarVariaveis = (variaveis) => {
  if (!variaveis || typeof variaveis !== "object" || Array.isArray(variaveis)) return [];
  return Object.entries(variaveis).filter(([, valor]) => valor != null && String(valor).trim() !== "");
};

export default function NotificacoesPage({ userRole, onOpenPedidos }) {
  const isSuperadmin = userRole === "superadmin";
  const canManageOptOut = ["admin", "superadmin"].includes(userRole);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandido, setExpandido] = useState(null);
  const [reenviando, setReenviando] = useState(null);
  const [reativando, setReativando] = useState(null);
  const [textoCopiado, setTextoCopiado] = useState(null);
  const [erro, setErro] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clientesCarregados, setClientesCarregados] = useState(!isSuperadmin);
  const [clienteId, setClienteId] = useState(() => (
    isSuperadmin ? localStorage.getItem("rotaflex_notifications_cliente_id") || "" : ""
  ));

  const [filtros, setFiltros] = useState({
    status: "",
    search: "",
    data_inicio: "",
    data_fim: "",
  });
  const [aplicados, setAplicados] = useState({ status: "", search: "", data_inicio: "", data_fim: "" });
  const displayError = useCallback((message) => {
    recordUiError({ message, page: "notificacoes", source: "inline_error" });
    setErro(message);
  }, []);

  useEffect(() => {
    if (!isSuperadmin) return undefined;
    let active = true;
    apiFetch("/clientes-rotaflex")
      .then((rows) => {
        if (!active) return;
        const list = Array.isArray(rows) ? rows : [];
        setClientes(list);
        setClienteId((current) => {
          const valid = list.some((cliente) => String(cliente.id) === String(current));
          const next = valid ? String(current) : String(list[0]?.id || "");
          if (next) localStorage.setItem("rotaflex_notifications_cliente_id", next);
          return next;
        });
      })
      .catch((error) => {
        if (active) displayError(error?.message || "Erro ao carregar clientes.");
      })
      .finally(() => {
        if (active) setClientesCarregados(true);
      });
    return () => { active = false; };
  }, [displayError, isSuperadmin]);

  const carregar = useCallback(async () => {
    if (isSuperadmin && (!clientesCarregados || !clienteId)) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const params = new URLSearchParams();
      if (isSuperadmin) params.set("cliente_id", clienteId);
      if (aplicados.status) params.set("status", aplicados.status);
      if (aplicados.search) params.set("search", aplicados.search);
      if (aplicados.data_inicio) params.set("data_inicio", aplicados.data_inicio);
      if (aplicados.data_fim) params.set("data_fim", aplicados.data_fim);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      const data = await apiFetch(`/notifications?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      displayError(e?.message || "Erro ao carregar notificações.");
    } finally {
      setLoading(false);
    }
  }, [aplicados, clienteId, clientesCarregados, displayError, isSuperadmin, offset]);

  useEffect(() => { carregar(); }, [carregar]);

  const aplicarFiltros = () => {
    setOffset(0);
    setAplicados({ ...filtros });
  };
  const limparFiltros = () => {
    const vazio = { status: "", search: "", data_inicio: "", data_fim: "" };
    setFiltros(vazio);
    setAplicados(vazio);
    setOffset(0);
  };

  const reenviar = async (id) => {
    setReenviando(id);
    try {
      const tenantQuery = isSuperadmin ? `?cliente_id=${encodeURIComponent(clienteId)}` : "";
      await apiFetch(`/notifications/${id}/resend${tenantQuery}`, { method: "POST" });
      await carregar();
    } catch (e) {
      displayError(e?.message || "Erro ao reenviar.");
    } finally {
      setReenviando(null);
    }
  };

  const podeReenviar = (notification) => (
    (
      ["failed", "pending", "queued"].includes(notification?.status)
      || (
        notification?.status === "cancelled"
        && notification?.erro_code === "RECIPIENT_OPT_OUT"
        && !notification?.opt_out_id
      )
    )
    && !notification?.fallback_notification_id
    && notification?.erro_code !== "INVALID_RECIPIENT_PHONE"
  );

  const copiarMensagem = async (notification) => {
    try {
      await navigator.clipboard.writeText(notification.mensagem_renderizada);
      setTextoCopiado(notification.id);
      window.setTimeout(() => setTextoCopiado((current) => (current === notification.id ? null : current)), 1800);
    } catch {
      displayError("Não foi possível copiar o texto da mensagem.");
    }
  };

  const registrarReativacao = async (notification) => {
    const motivo = window.prompt(
      "Informe como e por quem o consentimento para voltar a receber foi confirmado. Essa justificativa ficará auditada:"
    );
    if (motivo == null) return;
    if (motivo.trim().length < 10) {
      displayError("A justificativa da reativação deve ter pelo menos 10 caracteres.");
      return;
    }
    const confirmado = window.confirm(
      "Confirma que o próprio destinatário autorizou voltar a receber mensagens? Nenhuma mensagem será reenviada automaticamente."
    );
    if (!confirmado) return;
    setReativando(notification.opt_out_id);
    setErro(null);
    try {
      const tenantQuery = isSuperadmin ? `?cliente_id=${encodeURIComponent(clienteId)}` : "";
      await apiFetch(`/notifications/opt-outs/${notification.opt_out_id}/revoke${tenantQuery}`, {
        method: "POST",
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      await carregar();
    } catch (e) {
      displayError(e?.message || "Não foi possível registrar a reativação.");
    } finally {
      setReativando(null);
    }
  };
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <PageTitle icon="bell">Central de notificações</PageTitle>
        <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: '0.9375rem' }}>
          Confira se as mensagens foram enviadas, entregues ou precisam de atenção. {total} {total === 1 ? "registro" : "registros"}
          {total > PAGE_SIZE ? ` · página ${page}/${totalPages}` : ""}.
        </p>
      </div>

      <div style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ color: "var(--text)", fontSize: '0.9375rem', lineHeight: 1.55 }}>
          <strong>Entrega não é resposta.</strong> “Lida” confirma apenas que a mensagem foi aberta no WhatsApp.
          A resposta 1/2 do motorista é acompanhada em <strong>Pedidos → Em operação</strong>.
        </div>
        {typeof onOpenPedidos === "function" && (
          <Btn variant="secondary"
            type="button"
            onClick={() => onOpenPedidos({ tab: "operacao" })}
            style={{ padding: "7px 12px", minHeight: 44, borderRadius: 8, border: "1px solid rgba(59,130,246,0.45)", background: "transparent", color: "#93c5fd", fontWeight: 700, cursor: "pointer", fontSize: '0.875rem', whiteSpace: "nowrap" }}
          >
            Ver respostas dos motoristas
          </Btn>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, alignItems: "flex-end" }}>
        {isSuperadmin && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 220 }}>
            <label htmlFor="notifications-cliente" style={{ fontSize: '0.8125rem', color: "var(--muted)", fontWeight: 600 }}>Cliente</label>
            <select
              id="notifications-cliente"
              value={clienteId}
              onChange={(event) => {
                const next = event.target.value;
                setClienteId(next);
                setOffset(0);
                if (next) localStorage.setItem("rotaflex_notifications_cliente_id", next);
              }}
              disabled={!clientesCarregados}
              style={{ padding: "8px 10px", minHeight: 44, borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", fontSize: '1rem' }}
            >
              <option value="">Selecione o cliente</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>{cliente.nome}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor="notifications-status" style={{ fontSize: '0.8125rem', color: "var(--muted)", fontWeight: 600 }}>Status</label>
          <select
            id="notifications-status"
            value={filtros.status}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
            style={{ padding: "8px 10px", minHeight: 44, borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", fontSize: '1rem' }}
          >
            {STATUS_FILTROS.map((s) => (
              <option key={s || "todos"} value={s}>{s ? (STATUS_CONFIG[s]?.label || s) : "Todos"}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 200 }}>
          <label htmlFor="notifications-search" style={{ fontSize: '0.8125rem', color: "var(--muted)", fontWeight: 600 }}>Nome ou telefone</label>
          <input
            id="notifications-search"
            value={filtros.search}
            onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
            placeholder="Ex: Carlos ou 5516999..."
            style={{ padding: "8px 10px", minHeight: 44, borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", fontSize: '1rem' }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor="notifications-start" style={{ fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 500 }}>De</label>
          <input id="notifications-start" type="date" value={filtros.data_inicio} onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
            style={{ padding: "8px 10px", minHeight: 44, borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", fontSize: '1rem' }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label htmlFor="notifications-end" style={{ fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 500 }}>Até</label>
          <input id="notifications-end" type="date" value={filtros.data_fim} onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
            style={{ padding: "8px 10px", minHeight: 44, borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", fontSize: '1rem' }} />
        </div>
        <Btn variant="primary" type="button" onClick={aplicarFiltros}>
          Filtrar
        </Btn>
        <Btn variant="secondary" type="button" onClick={limparFiltros}>
          Limpar
        </Btn>
      </div>

      {erro && (
        <div style={{ padding: 12, marginBottom: 12, borderRadius: 8, background: "rgba(239,68,68,.12)", color: "#fca5a5", fontSize: '1rem' }}>{erro}</div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Carregando…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>
          <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>Nenhuma mensagem encontrada</div>
          <p style={{ marginBottom: 12 }}>As mensagens aparecem aqui após o envio das vans. Se você já enviou, confira o período e os filtros.</p>
          <Btn variant="secondary" type="button" onClick={limparFiltros}>Limpar filtros</Btn>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, flexWrap: "wrap" }}>
          {items.map((n) => {
            const sc = STATUS_CONFIG[n.status] || { label: n.status, color: "#cbd5e1", bg: "rgba(148,163,184,.12)" };
            const aberto = expandido === n.id;
            const erroOperacional = getErroOperacional(n);
            const variaveisRegistradas = listarVariaveis(n.variaveis);
            return (
              <div key={n.id} style={{ border: "1px solid var(--border)", borderRadius: 10, background: "var(--s1)", overflow: "hidden" }}>
                <button type="button" className="rf-notification-trigger"
                  aria-expanded={aberto} aria-controls={`notification-${n.id}`}
                  style={{ padding: "16px", cursor: "pointer", textAlign: "left", width: "100%", background: "transparent", color: "var(--text)" }}
                  onClick={() => setExpandido(aberto ? null : n.id)}
                >
                  <Icon name="send" size={18} />
                  <span className="rf-notification-identity" style={{ minWidth: 0 }}>
                    <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: '1rem' }}>{n.destinatario_nome || "—"}</span>
                      <span style={{ color: "var(--muted)", fontSize: '0.875rem' }}>{formatarTelefone(n.destinatario_phone)}</span>
                      {n.fallback_of_notification_id && (
                        <span style={{ padding: "2px 7px", borderRadius: 999, background: "rgba(234,179,8,0.15)", color: "#fde047", fontSize: '0.8125rem', fontWeight: 700 }}>
                          TELEFONE SECUNDÁRIO
                        </span>
                      )}
                    </span>
                    <span style={{ display: "block", color: "var(--muted)", fontSize: '0.875rem', marginTop: 2 }}>
                      Mensagem operacional · {formatarData(n.criado_em)}
                    </span>
                  </span>
                  <span className="rf-notification-status" style={{ padding: "4px 10px", borderRadius: 999, background: sc.bg, color: sc.color, fontSize: '0.875rem', fontWeight: 700 }}>{sc.label}</span>
                  {erroOperacional && <span className="rf-notification-error" title={erroOperacional}><Icon name="warning" /> {erroOperacional}</span>}
                  <span className="rf-notification-chevron"><Icon name="chevron-down" size={18} style={{ transform: aberto ? 'rotate(180deg)' : undefined }} />{aberto ? 'Recolher detalhes' : 'Ver detalhes'}</span>
                </button>

                {aberto && (
                  <div id={`notification-${n.id}`} style={{ padding: "0 14px 14px", borderTop: "1px solid var(--border)" }}>
                    <div className="rf-notification-details" style={{ gap: "8px 16px", marginTop: 12, fontSize: '0.9375rem' }}>
                      <div><span style={{ color: "var(--muted)" }}>Modelo de mensagem:</span> {n.template_key || "—"}</div>
                      <div><span style={{ color: "var(--muted)" }}>Provedor:</span> {n.provider || "—"}</div>
                      <div><span style={{ color: "var(--muted)" }}>ID da mensagem:</span> <code style={{ fontSize: '0.8125rem' }}>{n.provider_message_id || "—"}</code></div>
                      <div><span style={{ color: "var(--muted)" }}>Tentativas:</span> {n.tentativas}/{n.max_attempts || "—"}</div>
                      <div><span style={{ color: "var(--muted)" }}>Aceita pela Meta:</span> {formatarData(n.accepted_at)}</div>
                      <div><span style={{ color: "var(--muted)" }}>Enviada ao WhatsApp:</span> {formatarData(n.sent_at || n.enviada_em)}</div>
                      <div><span style={{ color: "var(--muted)" }}>Entregue:</span> {formatarData(n.delivered_at)}</div>
                      <div>
                        <span style={{ color: "var(--muted)" }}>Lida no WhatsApp:</span> {formatarData(n.read_at)}
                        {n.read_at && <div style={{ marginTop: 2, color: "#fcd34d", fontSize: '0.8125rem' }}>Não significa que o motorista respondeu.</div>}
                      </div>
                      <div><span style={{ color: "var(--muted)" }}>Falha:</span> {formatarData(n.failed_at)}</div>
                      <div><span style={{ color: "var(--muted)" }}>Intervenção necessária:</span> {formatarData(n.dead_letter_at)}</div>
                      <div><span style={{ color: "var(--muted)" }}>Atualizada em:</span> {formatarData(n.atualizado_em)}</div>
                      {n.erro_code && <div><span style={{ color: "var(--muted)" }}>Código do erro:</span> {n.erro_code}</div>}
                      {n.fallback_phone && <div><span style={{ color: "var(--muted)" }}>Telefone secundário:</span> {formatarTelefone(n.fallback_phone)}</div>}
                      {n.fallback_triggered_at && <div><span style={{ color: "var(--muted)" }}>Telefone secundário acionado:</span> {formatarData(n.fallback_triggered_at)}</div>}
                    </div>
                    {erroOperacional && (
                      <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "rgba(239,68,68,.12)", color: "#fca5a5", fontSize: '0.875rem' }}>
                        {erroOperacional}
                        {n.erro_code === "RECIPIENT_OPT_OUT" && n.opt_out_id && (
                          <div style={{ marginTop: 8, color: "#fecaca", lineHeight: 1.5 }}>
                            <div><strong>Bloqueio ativo desde:</strong> {formatarData(n.opt_out_criado_em)}</div>
                            <div><strong>Origem:</strong> {n.opt_out_origem || "—"}</div>
                            {n.opt_out_mensagem_recebida && (
                              <div><strong>Mensagem recebida:</strong> “{n.opt_out_mensagem_recebida}”</div>
                            )}
                            {n.opt_out_motivo && <div><strong>Registro:</strong> {n.opt_out_motivo}</div>}
                            <div style={{ marginTop: 5, fontWeight: 700 }}>
                              O próprio número pode reativar imediatamente respondendo VOLTAR.
                            </div>
                          </div>
                        )}
                        {n.erro_code === "RECIPIENT_OPT_OUT" && !n.opt_out_id && (
                          <div style={{ marginTop: 5, fontWeight: 700 }}>
                            O bloqueio foi revogado. Se ainda for necessário, reenvie somente uma das notificações canceladas.
                          </div>
                        )}
                        {n.erro_code === "INVALID_RECIPIENT_PHONE" && (
                          <div style={{ marginTop: 5, fontWeight: 700 }}>
                            Depois de corrigir o cadastro, use “Reenviar notificações” na rota para gerar uma nova tentativa com o número atualizado.
                          </div>
                        )}
                        {n.fallback_notification_id && (
                          <div style={{ marginTop: 5, fontWeight: 700 }}>
                            O telefone secundário foi enfileirado automaticamente.
                          </div>
                        )}
                      </div>
                    )}
                    {n.mensagem_renderizada ? (
                      <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <div>
                            <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>Texto da mensagem</div>
                            <div style={{ color: "var(--muted)", fontSize: '0.8125rem', marginTop: 2 }}>
                              Texto e dados usados nesta tentativa de envio pelo WhatsApp.
                            </div>
                          </div>
                          <Btn variant="secondary"
                            type="button"
                            onClick={() => copiarMensagem(n)}
                            style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", cursor: "pointer", fontSize: '0.8125rem', whiteSpace: "nowrap" }}
                          >
                            {textoCopiado === n.id ? "Copiado ✓" : "Copiar texto"}
                          </Btn>
                        </div>
                        <pre style={{ margin: 0, color: "var(--text)", fontFamily: "inherit", fontSize: '0.875rem', lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {n.mensagem_renderizada}
                        </pre>
                      </div>
                    ) : variaveisRegistradas.length > 0 ? (
                      <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: "1px solid rgba(234,179,8,0.35)", background: "rgba(234,179,8,0.08)" }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 700, color: "#fde047" }}>Valores enviados ao template</div>
                        <div style={{ color: "var(--muted)", fontSize: '0.8125rem', marginTop: 3, lineHeight: 1.45 }}>
                          O texto completo desta mensagem não foi registrado. Os dados disponíveis aparecem abaixo.
                        </div>
                        <div style={{ display: "grid", gap: 5, marginTop: 9 }}>
                          {variaveisRegistradas.map(([nome, valor]) => (
                            <div key={nome} style={{ fontSize: '0.875rem', overflowWrap: "anywhere" }}>
                              <span style={{ color: "var(--muted)" }}>{formatarNomeVariavel(nome)}:</span> {String(valor)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {canManageOptOut && n.opt_out_id && (
                        <Btn variant="secondary"
                          type="button"
                          onClick={() => registrarReativacao(n)}
                          disabled={reativando === n.opt_out_id}
                          style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #b45309", background: "transparent", color: "#f59e0b", fontWeight: 600, cursor: reativando === n.opt_out_id ? "wait" : "pointer", fontSize: '0.9375rem' }}
                        >
                          {reativando === n.opt_out_id ? "Registrando…" : "Registrar reativação com consentimento"}
                        </Btn>
                      )}
                      {podeReenviar(n) && (
                        <Btn variant="secondary"
                          type="button"
                          onClick={() => reenviar(n.id)}
                          disabled={reenviando === n.id}
                          style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid var(--accent)", background: reenviando === n.id ? "var(--accent)" : "transparent", color: "var(--accent)", fontWeight: 600, cursor: reenviando === n.id ? "wait" : "pointer", fontSize: '0.9375rem' }}
                        >
                          {reenviando === n.id ? "Reenviando…" : "Reenviar mensagem"}
                        </Btn>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16, alignItems: "center" }}>
          <Btn variant="secondary"
            type="button"
            disabled={offset <= 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", cursor: offset <= 0 ? "not-allowed" : "pointer", opacity: offset <= 0 ? 0.5 : 1 }}
          >
            ← Anterior
          </Btn>
          <span style={{ fontSize: '0.9375rem', color: "var(--muted)" }}>{page} / {totalPages}</span>
          <Btn variant="secondary"
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--control-border)", background: "var(--s1)", color: "var(--text)", cursor: offset + PAGE_SIZE >= total ? "not-allowed" : "pointer", opacity: offset + PAGE_SIZE >= total ? 0.5 : 1 }}
          >
            Próxima →
          </Btn>
        </div>
      )}
    </div>
  );
}
